/**
 * Chart palette and colour-assignment rules.
 *
 * The eight categorical slots below are validated against this app's actual
 * dark chart surface (#15151f), not a generic one:
 *
 *   lightness band .... PASS  (all 8 within L 0.48-0.67)
 *   chroma floor ...... PASS
 *   CVD separation .... PASS  (worst adjacent dE 8.4, protan)
 *   normal vision ..... PASS  (worst adjacent dE 19.3)
 *   contrast .......... PASS  (all 8 >= 3:1)
 *
 * ## The many-series problem, and why we do not do what the reference does
 *
 * The reference dashboards plot 16 strain gauges as 16 cycled hues. That fails
 * on its own terms: no eight-hue set can be extended to sixteen and stay
 * discriminable, so `SG01` and `SG09` end up as two similar purples and the
 * legend becomes the only way to read the chart. Colour is doing no work.
 *
 * Kinetix assigns colour by the job it is doing instead:
 *
 * - **<= 8 series** - fixed categorical order. Colour carries identity.
 * - **> 8 series** - a single-hue sequential ramp indexed by the channel's
 *   *physical position* along the deck. Colour then encodes something real
 *   (where the gauge is), adjacent girders read as adjacent shades, and
 *   identity comes from the crosshair tooltip and the legend rather than from
 *   sixteen hues that cannot be told apart anyway.
 * - **Many-series scatter** (the "Peak Strain Response" view) - an envelope
 *   band plus a median line, because at all-pairs discrimination even three
 *   categorical hues is the honest cap. See `EnvelopeChart`.
 *
 * Status colours are reserved for alarm severity and never used for a series.
 */

import { currentTheme } from "./theme";


/** Categorical slots, in fixed assignment order. Never cycled, never reordered. */
export const CATEGORICAL = [
  "#3987e5", // blue
  "#d95926", // orange
  "#199e70", // aqua
  "#c98500", // yellow
  "#d55181", // magenta
  "#008300", // green
  "#9085e9", // violet
  "#e66767", // red
] as const;

/**
 * Single-hue sequential ramp, light to dark, for magnitude and for position.
 * On a dark surface the darkest usable ordinal step is 600.
 */
export const SEQUENTIAL_BLUE = [
  "#cde2fb",
  "#b7d3f6",
  "#9ec5f4",
  "#86b6ef",
  "#6da7ec",
  "#5598e7",
  "#3987e5",
  "#2a78d6",
  "#256abf",
  "#1c5cab",
  "#184f95",
] as const;

/** Second sequential context, when two magnitude ramps share a view. */
export const SEQUENTIAL_ORANGE = [
  "#f7d3c0",
  "#f3bda1",
  "#eea683",
  "#e88f66",
  "#e2784a",
  "#d95926",
  "#c14d20",
  "#a3411b",
  "#853516",
] as const;

/** Reserved for state. Never reused as a series colour. */
export const STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
} as const;

/** Alarm severity 1 (most severe) .. 5, mapped onto the status ramp. */
export const SEVERITY: Record<number, string> = {
  1: STATUS.critical,
  2: STATUS.serious,
  3: STATUS.warning,
  4: "#3987e5",
  5: "#898781",
};

export const SEVERITY_LABEL: Record<number, string> = {
  1: "Critical",
  2: "Serious",
  3: "Warning",
  4: "Advisory",
  5: "Info",
};

/** Chart chrome. Every one of these is ink, never a series colour. */
/*
 * Chart chrome - axes, gridlines, labels - for each theme.
 *
 * ECharts draws on a canvas and cannot resolve a CSS variable, so the charts
 * cannot follow the theme the way the rest of the page does. These are the
 * same roles as the `ink` scale in index.css, as literal colours.
 */
const CHROME_BY_THEME = {
  dark: {
    surface: "#15151f",
    page: "#0a0a0f",
    /** Behind a chart's range slider - a step deeper than the card. */
    deep: "#101018",
    /** Raised above a card: the slider's filled window, hover rows. */
    raised: "#1b1b27",
    /** A tooltip's box. Its own role because it must invert WITH its text. */
    tooltip: "#1b1b27",
    primaryInk: "#e8e8f0",
    strongInk: "#c5c5d8",
    secondaryInk: "#9a9ab8",
    mutedInk: "#6b6b8f",
    gridline: "#26263a",
    axis: "#33334d",
  },
  light: {
    surface: "#ffffff",
    page: "#eef0f5",
    deep: "#f7f8fb",
    raised: "#eceef4",
    tooltip: "#ffffff",
    primaryInk: "#161a26",
    strongInk: "#2f3446",
    secondaryInk: "#4f5569",
    mutedInk: "#62687c",
    gridline: "#e3e6ee",
    axis: "#c3c7d4",
  },
} as const;

type ChromeKey = keyof (typeof CHROME_BY_THEME)["dark"];

/**
 * How an OpenStreetMap basemap is toned for the current theme.
 *
 * On dark, the tiles are dimmed and greyed so the markers - the actual content
 * - are what the eye lands on, and so the map sits on the dark cards instead
 * of being a bright rectangle in the middle of them. On light, the same
 * darkening turns a map muddy grey against white, so it keeps the tiles close
 * to their natural brightness and only takes some of the colour out.
 *
 * `strength` is how dark each map wants to go on the dark theme: the project
 * overview goes darker than a site map, where the streets are the point.
 */
export function basemapPaint(strength: "strong" | "soft" = "soft"): Record<string, number> {
  if (currentTheme() === "light") {
    return { "raster-saturation": -0.45, "raster-brightness-max": 1, "raster-contrast": 0 };
  }
  return strength === "strong"
    ? { "raster-saturation": -0.85, "raster-brightness-max": 0.42, "raster-contrast": 0.1 }
    : { "raster-saturation": -0.7, "raster-brightness-max": 0.6 };
}

/**
 * The current theme's chrome.
 *
 * Getters, not values, so each of the hundred-odd `CHROME.gridline` reads in
 * the chart code resolves against the theme at the moment the option is
 * built - without any of them being edited, and without a second palette that
 * someone later forgets to update. A chart rebuilds its option when the theme
 * changes because Layout remounts the page under it; see there.
 */
export const CHROME = Object.defineProperties(
  {} as { readonly [K in ChromeKey]: string },
  Object.fromEntries(
    (Object.keys(CHROME_BY_THEME.dark) as ChromeKey[]).map((k) => [
      k,
      { get: () => CHROME_BY_THEME[currentTheme()][k], enumerable: true },
    ]),
  ),
);

/**
 * Colour for series `index` of `total`.
 *
 * The switch at 8 is the whole point: past it, hue stops meaning "which one"
 * and starts meaning "where along the structure".
 */
export function seriesColor(index: number, total: number): string {
  if (total <= CATEGORICAL.length) {
    return CATEGORICAL[index % CATEGORICAL.length];
  }
  // Spread across the readable part of the ramp; the two lightest steps are
  // reserved for "near zero" in true sequential use and recede on this surface.
  const usable = SEQUENTIAL_BLUE.slice(2);
  const position = total <= 1 ? 0 : index / (total - 1);
  return usable[Math.round(position * (usable.length - 1))];
}

/** Whether colour is carrying identity, which decides if a legend can stand alone. */
export function colorCarriesIdentity(total: number): boolean {
  return total <= CATEGORICAL.length;
}

/**
 * Colour by physical quantity, so strain is always the same hue across every
 * chart in the app and a reader builds one mental mapping rather than one per
 * screen.
 */
export const BY_FIELD: Record<string, string> = {
  Strain: CATEGORICAL[0],
  Acceleration: CATEGORICAL[1],
  Tilt: CATEGORICAL[2],
  Temperature: CATEGORICAL[3],
  Velocity: CATEGORICAL[4],
  Mass: CATEGORICAL[6],
};

export function fieldColor(field: string | null | undefined, fallbackIndex = 0): string {
  if (field && BY_FIELD[field]) return BY_FIELD[field];
  return CATEGORICAL[fallbackIndex % CATEGORICAL.length];
}
